import React from 'react';
import { motion } from 'framer-motion';
import { X, Check, Cpu, Zap, Globe2, Database } from 'lucide-react';
export function ProfessionalComparison() {
  const comparisons = [{
    situation: 'Enseñar SMR y el mercado global',
    without: 'Sabés que existen pero no tenés material técnico profundo',
    with: 'Los 6 SMR más avanzados del mundo comparados, el CAREM-25 en perspectiva global y los 4 segmentos del mercado'
  }, {
    situation: 'Hablar de Inteligencia Artificial y nuclear',
    without: 'No conectás IA con la industria nuclear argentina',
    with: 'Mantenimiento predictivo, optimización de combustible, diseño de materiales con ML y el círculo virtuoso IA-nuclear'
  }, {
    situation: 'Explicar la fusión nuclear actual',
    without: 'Mencionás ITER pero no podés ir más lejos',
    with: 'Estado del arte completo: NIF (ignición 2022), ITER 2026, empresas privadas, Commonwealth Fusion, Helion y TAE Technologies'
  }, {
    situation: 'Conectar el litio argentino con el futuro',
    without: 'No sabés que hay conexión con la fusión',
    with: 'El triángulo del litio argentino como recurso estratégico para el tritio de la fusión nuclear'
  }, {
    situation: 'Hablar de Big Tech y energía nuclear',
    without: 'No sabés que Microsoft compró energía nuclear para su IA',
    with: 'Los contratos reales de Google, Microsoft y Amazon con proveedores nucleares explicados para el aula'
  }, {
    situation: 'Posicionarte como docente de vanguardia',
    without: 'Tu material es el mismo que el de todos',
    with: 'Podés enseñar lo que ningún libro de texto del mundo tiene todavía'
  }];
  const modules = [{
    icon: <Globe2 className="w-6 h-6" data-id="element-246" />,
    title: 'SMR: El Mercado Global de Reactores Modulares en 2026',
    color: 'text-electric',
    bgColor: 'bg-electric/20',
    items: 'El panorama completo de los 6 SMR más avanzados del mundo, por qué el CAREM-25 argentino está en una posición única a nivel global, los cuatro segmentos del mercado (países en desarrollo, industria, centros de datos de IA, reemplazo de carbón), las barreras que el mercado debe superar y la estrategia exportadora argentina con sus ventajas y desafíos reales.'
  }, {
    icon: <Cpu className="w-6 h-6" data-id="element-247" />,
    title: 'Inteligencia Artificial en la Industria Nuclear',
    color: 'text-exclusive',
    bgColor: 'bg-exclusive/20',
    items: 'Mantenimiento predictivo con machine learning, optimización de carga de combustible con algoritmos evolutivos, monitoreo de seguridad con IA (la respuesta técnica al problema de TMI), diseño de nuevos materiales nucleares computacionalmente, y el círculo virtuoso completo: por qué Microsoft reactivó Three Mile Island para alimentar sus centros de datos de IA.'
  }, {
    icon: <Zap className="w-6 h-6" data-id="element-248" />,
    title: 'Fusión Nuclear: El Estado del Arte Completo 2026',
    color: 'text-neon',
    bgColor: 'bg-neon/20',
    items: 'Análisis técnico completo del hito del NIF (diciembre 2022: Q>1 por primera vez en la historia), el estado de ITER en 2026 y su cronograma actualizado, los dos caminos de la fusión (confinamiento magnético vs inercial), el tritio como recurso escaso y el rol estratégico del litio argentino para el breeding, y el horizonte temporal honesto de la fusión comercial con sus tres escenarios posibles.'
  }, {
    icon: <Database className="w-6 h-6" data-id="element-249" />,
    title: 'Las Empresas Privadas de Fusión',
    color: 'text-electric',
    bgColor: 'bg-electric/20',
    items: 'Perfiles completos de Commonwealth Fusion Systems (SPARC con imanes HTS), Helion Energy (el contrato firmado con Microsoft para 2028), TAE Technologies (fusión p-B11 aneutrónica) y General Fusion (compresión mecánica magnetizada). Análisis honesto del cronograma: escenario optimista, moderado y pesimista para la fusión comercial.'
  }];
  return <section className="py-20 bg-base" data-id="element-250">
      <div className="container mx-auto px-4 max-w-5xl" data-id="element-251">
        <div className="text-center mb-12" data-id="element-252">
          <h2 className="md:text-4xl font-extrabold text-white mb-4 text-[18px]" data-id="element-253">
            🎯 ¿QUÉ AGREGA EL UPSELL PREMIUM?
          </h2>
        </div>

        {/* Comparison Table */}
        <div className="mb-16 overflow-x-auto pb-4" data-id="element-254">
          <div className="min-w-[800px]" data-id="element-255">
            <div className="grid grid-cols-3 gap-4 mb-4 px-4" data-id="element-256">
              <div className="font-bold text-muted uppercase text-sm tracking-wider" data-id="element-257">
                Situación
              </div>
              <div className="font-bold text-red-400 uppercase text-sm tracking-wider flex items-center gap-2" data-id="element-258">
                <X className="w-4 h-4" data-id="element-259" /> Sin Upsell Premium
              </div>
              <div className="font-bold text-exclusive uppercase text-sm tracking-wider flex items-center gap-2" data-id="element-260">
                <Check className="w-4 h-4" data-id="element-261" /> Con Upsell Premium
              </div>
            </div>

            <div className="space-y-3" data-id="element-262">
              {comparisons.map((row, i) => <div key={i} className="grid grid-cols-3 gap-4 bg-surface rounded-xl p-4 items-center border border-surface" data-id="element-263">
                  <div className="font-medium text-white" data-id="element-264">{row.situation}</div>
                  <div className="text-muted text-sm" data-id="element-265">{row.without}</div>
                  <div className="text-white font-medium text-sm bg-exclusive/20 p-2 rounded-lg border border-exclusive/50" data-id="element-266">
                    {row.with}
                  </div>
                </div>)}
            </div>
          </div>
          <p className="text-center text-sm text-muted mt-4 md:hidden" data-id="element-267">
            ← Scroll to see full table →
          </p>
        </div>

        {/* Modules Grid */}
        <div className="bg-surface rounded-3xl p-8 md:p-12 text-white shadow-xl" data-id="element-268">
          <h3 className="font-bold mb-8 flex items-center gap-3 text-[16px]" data-id="element-269">
            <span className="bg-exclusive text-white p-1 rounded-full" data-id="element-270">
              <Check className="w-5 h-5" data-id="element-271" />
            </span>
            🚀 ¿QUÉ INCLUYE EL UPSELL PREMIUM?
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8" data-id="element-272">
            {modules.map((mod, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-base/50 rounded-2xl p-6 border border-surface" data-id="element-273">
                <div className="flex items-center gap-3 mb-4" data-id="element-274">
                  <div className={`p-2 rounded-lg ${mod.bgColor} ${mod.color}`} data-id="element-275">
                    {mod.icon}
                  </div>
                  <h4 className="font-bold text-lg" data-id="element-276">{mod.title}</h4>
                </div>
                <p className="text-muted text-sm leading-relaxed" data-id="element-277">
                  {mod.items}
                </p>
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
}